/*This file conatains the common Validations and Formatting functions
that are used by multiple sheets for participant data entires
Product specific formatting functions are in their respective 
validations and formatting files*/

import { format, isValid, isBefore } from 'date-fns'
import {
  parseDateFromString,
  findCountry,
  findUSState,
  validateTaxId,
  formatAndValidateCurrency,
  formatCurrency,
  formatAndValidatePercentage,
  convertToLowercase,
  convertToUppercase,
} from 'utils'
import * as yup from 'yup'
import { US_COUNTRY_VALUE } from 'config/locations'
import { invalidPostalCodes } from 'config/products'
import {
  BD_TRANSACTION_TYPES,
  PAYMENT_METHODS,
} from 'config/benefit-distributions'
import { notificationServicesFields } from '../config/notificationsFields'
import { RECORDKEEPER_RULES } from '../config/recordkeeper-rules'

const numericRegex = /\d/g
const NARegex = /^NA$/
const specialCharacterRegex =
  /[\u2018\u2019\u201A\u201B\u201C\u201D\u201E\u201F\u2032\u2033\u0022\u0060\u00B4\u0027]/g
const longDashRegex = /[\u00A0\s]–[\u00A0\s]/g

const ssnFormatRegex = /^\d{3}-\d{2}-\d{4}[a-zA-Z]?$/
const dobFormatRegex = /^\d{4}\/\d{2}\/\d{2}$/
const zipCodeFormatRegex = /^\d{5}(?:-\d{4})?$/

export const formatPlanEin = (record) => {
  const planEin = record.get('planEin')
  if (planEin) {
    const sanitizedPlanEin = planEin.match(numericRegex)?.join('')
    if (sanitizedPlanEin) {
      record.set('planEin', sanitizedPlanEin.replace(/(\d{2})(\d+)/, '$1-$2'))
    }
  }
  return record
}

export const formatPlanSponsor = (record) => {
  const planSponsor = record.get('planSponsor')
  if (planSponsor) {
    const sanitizedPlanSponsor = planSponsor
      .match(specialCharacterRegex)
      ?.join('')
    if (sanitizedPlanSponsor) {
      record.set('planSponsor', planSponsor.replace(specialCharacterRegex, "'"))
    }
  }
  return record
}

export function formatPlanName(record) {
  const raw = record.get('planName')
  if (!raw) return

  const trimmed = raw.trim()

  let formatted = trimmed.replace(specialCharacterRegex, "'")
  formatted = formatted.replace(longDashRegex, '-')

  record.set('planName', formatted)
}

export function formatPlanIdNumber(record) {
  const planIdNumber = record.get('planIdNumber')
  if (!planIdNumber) return

  const sanitizedPlanIdNumber = planIdNumber.replaceAll(/[^a-zA-Z0-9-]/g, '')
  record.set('planIdNumber', sanitizedPlanIdNumber)
}

export const formatPlanNumber = (record) => {
  const planNumber = record.get('planNumber')
  if (!planNumber) return

  const sanitizedPlanNumber = planNumber.match(numericRegex)?.join('')
  if (sanitizedPlanNumber) {
    const sanitizedInput = sanitizedPlanNumber.replace(/(\d{3})/, '$1')
    record.set('planNumber', sanitizedInput)
  }
}

export const formatZipCode = (record) => {
  const zipCode = record.get('zipCode')
  if (!zipCode) return

  const sanitizedInput = zipCode.replaceAll(/[^a-zA-Z0-9- ]/g, '')
  let sanitizedZipCode = sanitizedInput.match(numericRegex)?.join('')
  if (sanitizedZipCode) {
    record.set('zipCode', sanitizedZipCode)
    if (sanitizedZipCode.length === 4 || sanitizedZipCode.length === 8) {
      sanitizedZipCode = '0' + sanitizedZipCode
      record.set('zipCode', sanitizedZipCode)
    } else if (sanitizedZipCode.length === 3 || sanitizedZipCode.length === 7) {
      sanitizedZipCode = '00' + sanitizedZipCode
      record.set('zipCode', sanitizedZipCode)
    } else if (sanitizedZipCode.length === 2 || sanitizedZipCode.length === 6) {
      sanitizedZipCode = '000' + sanitizedZipCode
      record.set('zipCode', sanitizedZipCode)
    }
  }
  if (sanitizedZipCode && sanitizedZipCode.length > 5) {
    record.set(
      'zipCode',
      sanitizedZipCode.slice(0, 5) + '-' + sanitizedZipCode.slice(5)
    )
  }
}

const validateDobAge = (dobDate, record) => {
  const currentDate = new Date()
  const maxDate = new Date(currentDate)
  maxDate.setFullYear(currentDate.getFullYear() - 100)
  const minDate = new Date(currentDate)
  minDate.setFullYear(currentDate.getFullYear() - 10)

  if (dobDate > minDate) {
    record.addError('dob', 'Must be over 10 years.')
  } else if (isBefore(dobDate, maxDate)) {
    record.addError('dob', 'Must be under 100 years.')
  }
}

const validateDobFormat = (dob, record, dobFormat) => {
  dobFormat.test(dob)
    ? record.addError('dob', 'Must be a valid date')
    : record.addError('dob', 'Format must be yyyy/mm/dd')
}

export const validateDob = (record, ctx) => {
  const dob = record.get('dob')
  if (dob) {
    if (NARegex.test(dob) || dob.trim() === '') {
      record.addError('dob', 'Required.')
    } else {
      const dobDate = new Date(dob)
      if (isValid(dobDate)) {
        validateDobAge(dobDate, record)
      } else {
        const dobFormat = ctx?.dobFormat ?? dobFormatRegex
        validateDobFormat(dob, record, dobFormat)
      }
    }
  }
  return record
}

export const formatDob = (record) => {
  const dob = record.get('dob')
  if (dob) {
    const dobDate = parseDateFromString(dob.trim())
    let formattedDob = dob
    if (dobDate) {
      formattedDob = format(dobDate, 'yyyy/MM/dd')
      record.set('dob', formattedDob)
    } else {
      const sanitizedDob = dob.match(numericRegex)?.join('')
      if (sanitizedDob) {
        formattedDob = sanitizedDob.replace(/(\d{4})(\d{2})(\d{2})/, '$1/$2/$3')
        record.set('dob', formattedDob)
      }
    }
  }
  return record
}

export const formatSsn = (record) => {
  const ssn = record.get('ssn')
  if (ssn) {
    const firstLetter = ssn.match(/((\d\D*?){9})([a-zA-Z])/)?.[3] || ''
    const sanitizedSsn = ssn.match(numericRegex)?.join('')
    if (sanitizedSsn) {
      const finalizedSsn =
        sanitizedSsn.replace(/(\d{3})(\d{2})(\d{4})/, '$1-$2-$3') + firstLetter
      record.set('ssn', finalizedSsn)
    }
  }
  return record
}

export const validateSsn = (record, ctx) => {
  const ssn = record.get('ssn')
  if (ssn) {
    if (NARegex.test(ssn)) {
      record.addError('ssn', 'Required.')
    } else {
      const ssnFormat = ctx?.ssnFormat ?? ssnFormatRegex
      if (!ssnFormat.test(ssn)) {
        record.addError('ssn', 'Format must be XXX-XX-XXXX')
      } else if (!validateTaxId(ssn)) {
        record.addError('ssn', 'Given SSN is not valid')
      }
    }
  }
  return record
}

export const formatCountryCode = (record) => {
  const countryCode = record.get('countryCode')
  if (countryCode) {
    const matchingCountry = findCountry(countryCode)
    if (matchingCountry) {
      record.set('countryCode', matchingCountry.value)
    } else {
      record.addError(
        'countryCode',
        'Must be a valid country name or country code'
      )
    }
  }
  return record
}

export const formatState = (record) => {
  const state = record.get('state')
  const countryCode = record.get('countryCode')
  if (state && countryCode === US_COUNTRY_VALUE) {
    const matchingState = findUSState(state)
    if (matchingState) {
      record.set('state', matchingState.value)
    } else {
      record.addError('state', 'Must be a valid US state')
    }
  }
  return record
}

export const validateEmailAddress = (record) => {
  const emailAddress = record.get('emailAddress')
  if (emailAddress) {
    const schema = yup.string().email()
    const isValidEmail = schema.isValidSync(emailAddress)
    if (!isValidEmail) {
      record.addError(
        'emailAddress',
        'Must be valid email format: example@email.com'
      )
    }
  }
  return record
}

const validateUSZipCode = (record, zipCode, ctx) => {
  const zipCodeFormat = ctx?.zipCodeFormat ?? zipCodeFormatRegex
  const postalCode = zipCode.substring(0, 5)
  if (!zipCodeFormat.test(zipCode)) {
    record.addError(
      'zipCode',
      'US Zip Codes must have format: XXXXX or XXXXX-XXXX'
    )
  } else if (invalidPostalCodes.includes(postalCode)) {
    record.addError('zipCode', 'Please enter a valid Zip Code')
  }
}

const validateUSCountry = (record, state, zipCode, ctx) => {
  if (!state) record.addError('state', 'Required.')
  if (zipCode) {
    validateUSZipCode(record, zipCode, ctx)
  } else {
    record.addError('zipCode', 'Required.')
  }
}

const validateNonUSCountry = (record, state) => {
  if (state && state.length > 22) {
    record.addError(
      'state',
      'Maximum length exceeded - must be 22 characters or less'
    )
  }
}

export const validateCountryCodeStateCityZip = (record, ctx) => {
  const countryCode = record.get('countryCode')
  const state = record.get('state')
  const zipCode = record.get('zipCode')

  if (countryCode === US_COUNTRY_VALUE) {
    validateUSCountry(record, state, zipCode, ctx)
  } else {
    validateNonUSCountry(record, state)
  }
  return record
}

export const formatTraditionalIraPreTaxAmount = (record) => {
  const traditionalIraPreTaxAmount = record.get('traditionalIraPreTaxAmount')
  if (traditionalIraPreTaxAmount) {
    formatAndValidateCurrency(
      traditionalIraPreTaxAmount,
      'traditionalIraPreTaxAmount',
      record
    )
  }
  return record
}

export const formatTraditionalIraAfterTaxAmount = (record) => {
  const traditionalIraAfterTaxAmount = record.get(
    'traditionalIraAfterTaxAmount'
  )
  if (traditionalIraAfterTaxAmount) {
    formatAndValidateCurrency(
      traditionalIraAfterTaxAmount,
      'traditionalIraAfterTaxAmount',
      record
    )
  }
  return record
}

export const formatRothIraAmount = (record) => {
  const rothIraAmount = record.get('rothIraAmount')
  if (rothIraAmount) {
    formatAndValidateCurrency(rothIraAmount, 'rothIraAmount', record)
  }
  return record
}

// ── Inherited IRA-specific pure functions ──────────────────────────────────────
// isNumeric: true when value contains only digits (no separators)
const isNumeric = (value) => value.replace(/\D/g, '') === value
// isNa: true when value is the string 'N/A' (case-insensitive)
const isNa = (value) =>
  typeof value === 'string' && value.toUpperCase() === 'N/A'

const validateDecedentDobAge = (dobDate, record) => {
  const currentDate = new Date()
  const maxDate = new Date(currentDate)
  maxDate.setFullYear(currentDate.getFullYear() - 100)
  const minDate = new Date(currentDate)
  minDate.setFullYear(currentDate.getFullYear() - 10)
  if (dobDate > minDate) {
    record.addError('decedentDateOfBirth', 'Must be over 10 years.')
  } else if (isBefore(dobDate, maxDate)) {
    record.addError('decedentDateOfBirth', 'Must be under 100 years.')
  }
}

const validateDecedentDobFormat = (dob, record, dobFormat) => {
  dobFormat.test(dob)
    ? record.addError('decedentDateOfBirth', 'Must be a valid date')
    : record.addError('decedentDateOfBirth', 'Format must be yyyy/mm/dd')
}

const validateDecedentDodFormat = (dod, record) => {
  const dateRe = /^\d{1,2}\/\d{1,2}\/\d{4}$/
  dateRe.test(dod)
    ? record.addError('decedentDateOfDeath', 'Must be a valid date')
    : record.addError(
        'decedentDateOfDeath',
        'Format must be single-digit M if applicable, otherwise MM/DD/YYYY'
      )
}

export const formatDecedentDob = (record) => {
  const dob = record.get('decedentDateOfBirth')
  if (dob) {
    const dobDate = parseDateFromString(dob.trim())
    if (dobDate) {
      record.set('decedentDateOfBirth', format(dobDate, 'yyyy/MM/dd'))
    } else {
      const sanitizedDob = dob.match(numericRegex)?.join('')
      if (sanitizedDob) {
        record.set(
          'decedentDateOfBirth',
          sanitizedDob.replace(/(\d{4})(\d{2})(\d{2})/, '$1/$2/$3')
        )
      }
    }
  }
  return record
}

export const validateDecedentDob = (record, ctx) => {
  const dob = record.get('decedentDateOfBirth')
  if (dob) {
    if (NARegex.test(dob) || dob.trim() === '') {
      record.addError('decedentDateOfBirth', 'Required.')
    } else {
      const dobDate = new Date(dob)
      if (isValid(dobDate)) {
        validateDecedentDobAge(dobDate, record)
      } else {
        const dobFormat = ctx?.dobFormat ?? dobFormatRegex
        validateDecedentDobFormat(dob, record, dobFormat)
      }
    }
  }
  return record
}

export const formatDecedentDod = (record) => {
  const dod = record.get('decedentDateOfDeath')
  if (dod) {
    const dodDate = isNumeric(dod) ? null : parseDateFromString(dod)
    if (dodDate) {
      record.set('decedentDateOfDeath', format(dodDate, 'M/d/yyyy'))
    } else {
      const sanitizedDod = dod.match(/[\d\\/]/g)?.join('')
      if (sanitizedDod && sanitizedDod !== '/') {
        record.set('decedentDateOfDeath', sanitizedDod)
      } else {
        record.set('decedentDateOfDeath', isNa(dod) ? dod.toUpperCase() : dod)
      }
    }
  }
  return record
}

export const validateDecedentDod = (record) => {
  const dod = record.get('decedentDateOfDeath')
  if (dod) {
    const dodDate = isNumeric(dod) ? null : new Date(dod)
    if (isValid(dodDate)) {
      if (dodDate > new Date()) {
        record.addError('decedentDateOfDeath', 'Cannot be in the future')
      } else if (isBefore(dodDate, new Date(0, 0, 1))) {
        record.addError('decedentDateOfDeath', 'Must be more recent than 1900')
      }
    } else if (!isNa(dod)) {
      validateDecedentDodFormat(dod, record)
    }
  }
  return record
}

// ── Uncashed Checks-specific pure functions ────────────────────────────────────

export const formatDateOfOriginalDistribution = (record) => {
  const dateOfOriginalDistribution = record.get('dateOfOriginalDistribution')
  if (dateOfOriginalDistribution) {
    const parsedDate = parseDateFromString(dateOfOriginalDistribution.trim())
    let formatted = dateOfOriginalDistribution

    if (parsedDate) {
      formatted = format(parsedDate, 'yyyy/MM/dd')
    } else {
      const sanitized = dateOfOriginalDistribution.match(numericRegex)?.join('')
      if (sanitized) {
        formatted = sanitized.replace(/(\d{4})(\d{1,2})(\d{2})/, '$1/$2/$3')
      }
    }
    record.set('dateOfOriginalDistribution', formatted)
  }
  return record
}

export const validateDateOfOriginalDistribution = (record) => {
  const dateOfOriginalDistribution = record.get('dateOfOriginalDistribution')
  if (dateOfOriginalDistribution) {
    const parsedDate = new Date(dateOfOriginalDistribution)
    if (isValid(parsedDate)) {
      const currentDate = new Date()
      const maxDate = new Date(currentDate)
      maxDate.setFullYear(currentDate.getFullYear() - 100)
      if (isBefore(parsedDate, maxDate)) {
        record.addError(
          'dateOfOriginalDistribution',
          'Must be under 100 years.'
        )
      }
    } else {
      const dateRe = /^\d{4}\/\d{1,2}\/\d{2}$/
      dateRe.test(dateOfOriginalDistribution)
        ? record.addError('dateOfOriginalDistribution', 'Must be a valid date')
        : record.addError(
            'dateOfOriginalDistribution',
            'Format must be single-digit M if applicable, otherwise YYYY/MM/DD'
          )
    }
  }
  return record
}

export const formatWasTaxWithheld = (record) => {
  const wasTaxWithheld = record.get('wasTaxWithheld')
  if (wasTaxWithheld) {
    record.set('wasTaxWithheld', wasTaxWithheld.toUpperCase())
  }
  return record
}

export const validateWasTaxWithheld = (record) => {
  const wasTaxWithheld = record.get('wasTaxWithheld')
  const traditionalIraPreTaxAmount = record.get('traditionalIraPreTaxAmount')
  if (!wasTaxWithheld && traditionalIraPreTaxAmount) {
    record.addError('wasTaxWithheld', 'Required.')
  }
  return record
}

// ── Search-specific pure functions ─────────────────────────────────────────────
// isNumeric and isNa are defined above in the Inherited IRA section and reused here.

export const formatSearchPlanSponsorName = (record) => {
  const planSponsorName = record.get('planSponsorName')
  if (planSponsorName) {
    const sanitized = planSponsorName.match(specialCharacterRegex)?.join('')
    if (sanitized) {
      record.set(
        'planSponsorName',
        planSponsorName.replace(specialCharacterRegex, "'")
      )
    }
  }
  return record
}

export const formatSearchDob = (record) => {
  const dob = record.get('dob')
  if (dob) {
    const dobDate = isNumeric(dob) ? null : parseDateFromString(dob)
    let formattedDob = dob

    if (dobDate) {
      formattedDob = format(dobDate, 'M/d/yyyy')
      record.set('dob', formattedDob)
    } else {
      const sanitizedDob = dob.match(/[0-9\\/]/g)?.join('')
      if (sanitizedDob && sanitizedDob !== '/') {
        formattedDob = sanitizedDob
        record.set('dob', formattedDob)
      } else {
        formattedDob = isNa(dob) ? dob.toUpperCase() : dob
        record.set('dob', formattedDob)
      }
    }
  }
  return record
}

export const formatSearchDod = (record) => {
  const dod = record.get('dod')
  if (dod) {
    const dodDate = isNumeric(dod) ? null : parseDateFromString(dod)
    if (dodDate) {
      record.set('dod', format(dodDate, 'M/d/yyyy'))
    } else {
      const sanitizedDod = dod.match(/[\d\\/]/g)?.join('')
      if (sanitizedDod && sanitizedDod !== '/') {
        record.set('dod', sanitizedDod)
      } else {
        record.set('dod', isNa(dod) ? dod.toUpperCase() : dod)
      }
    }
  }
  return record
}

export const formatSearchState = (record) => {
  const state = record.get('state')
  if (state) {
    const matchingState = findUSState(state)
    if (matchingState) {
      record.set('state', matchingState.value)
    } else {
      record.addError('state', 'Invalid US State abbreviation')
    }
  }
  return record
}

export const validateSearchSsn = (record, ctx) => {
  const ssn = record.get('ssn')
  if (ssn) {
    const ssnFormat = ctx?.ssnFormat ?? ssnFormatRegex
    if (!ssnFormat.test(ssn)) {
      record.addError('ssn', 'Format must be XXX-XX-XXXX')
    } else if (!validateTaxId(ssn)) {
      record.addError('ssn', 'Given SSN is not valid')
    }
  }
  return record
}

const searchDobFormatRegex = /^\d{1,2}\/\d{1,2}\/\d{4}$/

export const validateSearchDob = (record, ctx) => {
  const dob = record.get('dob')
  if (dob) {
    const dobDate = isNumeric(dob) ? null : new Date(dob)
    if (isValid(dobDate)) {
      const currentDate = new Date()
      const maxDate = new Date(currentDate)
      maxDate.setFullYear(currentDate.getFullYear() - 100)
      const minDate = new Date(currentDate)
      minDate.setFullYear(currentDate.getFullYear() - 10)
      if (dobDate > minDate) {
        record.addError('dob', 'Must be over 10 years.')
      } else if (isBefore(dobDate, maxDate)) {
        record.addError('dob', 'Must be under 100 years.')
      }
    } else if (!isNa(dob)) {
      const dobFormat = ctx?.dobFormat ?? searchDobFormatRegex
      dobFormat.test(dob)
        ? record.addError('dob', 'Must be a valid date')
        : record.addError(
            'dob',
            'Format must be single-digit M if applicable, otherwise MM/DD/YYYY'
          )
    }
  }
  return record
}

const searchDodFormatRegex = /^\d{1,2}\/\d{1,2}\/\d{4}$/

export const validateSearchDod = (record) => {
  const dod = record.get('dod')
  if (dod) {
    const dodDate = isNumeric(dod) ? null : new Date(dod)
    if (isValid(dodDate)) {
      if (dodDate > new Date()) {
        record.addError('dod', 'Cannot be in the future')
      } else if (isBefore(dodDate, new Date(0, 0, 1))) {
        record.addError('dod', 'Must be more recent than 1900')
      }
    } else if (!isNa(dod)) {
      searchDodFormatRegex.test(dod)
        ? record.addError('dod', 'Must be a valid date')
        : record.addError(
            'dod',
            'Format must be single-digit M if applicable, otherwise MM/DD/YYYY'
          )
    }
  }
  return record
}

export const validateSearchZipCode = (record) => {
  const zip = record.get('zipCode')
  if (zip) {
    const zipRe = /^\d{5}(?:-\d{4})?$/
    const postalCode = zip.substring(0, 5)
    if (!zipRe.test(zip)) {
      record.addError('zipCode', 'Zip must have format: XXXXX or XXXXX-XXXX')
    } else if (invalidPostalCodes.includes(postalCode)) {
      record.addError('zipCode', 'Please enter a valid Zip Code')
    }
  }
  return record
}

export const validateSearchAddressSsnCrossField = (record) => {
  const addressFields = ['address1', 'city', 'state', 'zipCode']
  const ssnField = 'ssn'

  const recordData = addressFields.reduce((acc, field) => {
    const value = record.get(field)
    acc[field] = value?.trim() === '' ? null : value
    return acc
  }, {})

  const ssnValue = record.get(ssnField)
  recordData[ssnField] = ssnValue?.trim() === '' ? null : ssnValue

  const hasFullAddress = addressFields.every(
    (field) => recordData[field] !== null
  )
  const hasSsn = recordData[ssnField] !== null

  if (!hasFullAddress && !hasSsn) {
    record.addError(ssnField, 'If no Address, required')
  }

  if (!hasSsn) {
    addressFields.forEach((field) => {
      if (
        recordData[field] === null ||
        recordData[field].toLowerCase() === 'n/a'
      ) {
        record.addError(field, 'If no SSN, required')
      }
    })
  }
  return record
}

// ── Benefit Distribution-specific pure functions ──────────────────────────────

// Strips non-numeric/decimal characters and parses as float (used for fee calculations)
const removeCurrencySymbolAndParse = (value) => {
  const sanitizedValue = value.replace(/[^\d.]/g, '')
  return Number.parseFloat(sanitizedValue) || 0
}

export const formatZipForCheck = (record) => {
  const zipForCheck = record.get('zipForCheck')
  if (zipForCheck) {
    const sanitizedInput = zipForCheck.replaceAll(/[^a-zA-Z0-9- ]/g, '')
    let sanitizedZipCode = sanitizedInput.match(numericRegex)?.join('')
    if (sanitizedZipCode) {
      record.set('zipForCheck', sanitizedZipCode)
      if (sanitizedZipCode.length === 4 || sanitizedZipCode.length === 8) {
        sanitizedZipCode = '0' + sanitizedZipCode
        record.set('zipForCheck', sanitizedZipCode)
      } else if (
        sanitizedZipCode.length === 3 ||
        sanitizedZipCode.length === 7
      ) {
        sanitizedZipCode = '00' + sanitizedZipCode
        record.set('zipForCheck', sanitizedZipCode)
      } else if (
        sanitizedZipCode.length === 2 ||
        sanitizedZipCode.length === 6
      ) {
        sanitizedZipCode = '000' + sanitizedZipCode
        record.set('zipForCheck', sanitizedZipCode)
      }
    }
    if (sanitizedZipCode && sanitizedZipCode.length > 5) {
      record.set(
        'zipForCheck',
        sanitizedZipCode.slice(0, 5) + '-' + sanitizedZipCode.slice(5)
      )
    }
  }
  return record
}

export const formatAmount = (record) => {
  const amount = record.get('amount')
  if (amount) {
    formatAndValidateCurrency(amount, 'amount', record)
  }
  return record
}

export const formatTpaBdProcessingFee = (record) => {
  const tpaBenefitDistributionProcessingFee = record.get(
    'tpaBenefitDistributionProcessingFee'
  )
  if (tpaBenefitDistributionProcessingFee) {
    formatAndValidateCurrency(
      tpaBenefitDistributionProcessingFee,
      'tpaBenefitDistributionProcessingFee',
      record
    )
  }
  return record
}

export const formatInspiraBdProcessingFee = (record) => {
  const transactionType = record.get('transactionType')
  if (!transactionType) return record
  if (
    transactionType.toLowerCase() === BD_TRANSACTION_TYPES.check.toLowerCase()
  ) {
    record.set('inspiraBenefitDisbursementProcessingFee', formatCurrency('35'))
  } else if (transactionType.toUpperCase() === BD_TRANSACTION_TYPES.ach) {
    record.set('inspiraBenefitDisbursementProcessingFee', formatCurrency('35'))
    record.set('transactionType', convertToUppercase(transactionType))
  } else if (
    [
      BD_TRANSACTION_TYPES.wire.toLocaleLowerCase(),
      BD_TRANSACTION_TYPES.overnight.toLocaleLowerCase(),
    ].includes(transactionType.toLowerCase())
  ) {
    record.set('inspiraBenefitDisbursementProcessingFee', formatCurrency('65'))
  } else {
    record.set('inspiraBenefitDisbursementProcessingFee', null)
  }
  return record
}

export const formatTransactionType = (record) => {
  const transactionType = record.get('transactionType')
  if (!transactionType) return record
  if (transactionType.toUpperCase() !== BD_TRANSACTION_TYPES.ach) {
    record.set('transactionType', convertToLowercase(transactionType))
  }
  return record
}

export const formatPaymentInstructions = (record) => {
  const paymentInstructions = record.get('paymentInstructions')
  if (paymentInstructions) {
    record.set('paymentInstructions', convertToLowercase(paymentInstructions))
  }
  return record
}

export const formatGrossDistributionAndAmount = (record, paymentMethod) => {
  const amount = record.get('amount')
  const tpaBenefitDistributionProcessingFee = record.get(
    'tpaBenefitDistributionProcessingFee'
  )
  const inspiraBenefitDisbursementProcessingFee = record.get(
    'inspiraBenefitDisbursementProcessingFee'
  )

  if (amount && inspiraBenefitDisbursementProcessingFee) {
    const amountInput = removeCurrencySymbolAndParse(amount)
    const inspiraFeeInput = removeCurrencySymbolAndParse(
      inspiraBenefitDisbursementProcessingFee
    )
    const tpaFeeInput = tpaBenefitDistributionProcessingFee
      ? removeCurrencySymbolAndParse(tpaBenefitDistributionProcessingFee)
      : 0

    const distributionAmountDD = (
      amountInput -
      (tpaFeeInput + inspiraFeeInput)
    ).toFixed(2)
    const distributionAmountCC = (amountInput - tpaFeeInput).toFixed(2)

    if (paymentMethod === PAYMENT_METHODS.deductFromDistribution) {
      formatAndValidateCurrency(
        distributionAmountDD.toString(),
        'grossDistributionAmount',
        record
      )
      formatAndValidateCurrency(
        distributionAmountDD.toString(),
        'grossDistribution',
        record
      )
    }
    if (paymentMethod === PAYMENT_METHODS.creditCard) {
      formatAndValidateCurrency(
        distributionAmountCC.toString(),
        'grossDistributionAmount',
        record
      )
      formatAndValidateCurrency(
        distributionAmountCC.toString(),
        'grossDistribution',
        record
      )
    }
  }
  return record
}

export const formatCapitalGain = (record) => {
  const capitalGain = record.get('capitalGain')
  if (capitalGain) {
    formatAndValidateCurrency(capitalGain, 'capitalGain', record)
  }
  return record
}

export const formatNetAppreciation = (record) => {
  const netAppreciation = record.get('netAppreciation')
  if (netAppreciation) {
    formatAndValidateCurrency(netAppreciation, 'netAppreciation', record)
  }
  return record
}

export const formatTotalEmployeeContributions = (record) => {
  const totalEmployeeContributions = record.get('totalEmployeeContributions')
  if (totalEmployeeContributions) {
    formatAndValidateCurrency(
      totalEmployeeContributions,
      'totalEmployeeContributions',
      record
    )
  }
  return record
}

export const formatAmountAllocable = (record) => {
  const amountAllocable = record.get('amountAllocable')
  if (amountAllocable) {
    formatAndValidateCurrency(amountAllocable, 'amountAllocable', record)
  }
  return record
}

export const formatStateForCheck = (record) => {
  const stateForCheck = record.get('stateForCheck')
  if (stateForCheck) {
    const matchingState = findUSState(stateForCheck)
    if (matchingState) {
      record.set('stateForCheck', matchingState.value)
    } else {
      record.addError('stateForCheck', 'Must be a valid US state')
    }
  }
  return record
}

export const formatPayerStateNumber = (record) => {
  const payerStateNumber = record.get('payerStateNumber')
  if (payerStateNumber) {
    const matchingState = findUSState(payerStateNumber)
    if (matchingState) {
      record.set('payerStateNumber', matchingState.value)
    } else {
      record.addError('payerStateNumber', 'Must be a valid US state')
    }
  }
  return record
}

export const formatTaxableAmount = (record) => {
  const taxableAmount = record.get('taxableAmount')
  if (taxableAmount) {
    formatAndValidateCurrency(taxableAmount, 'taxableAmount', record)
  }
  return record
}

export const formatTaxableAmountNotDetermined = (record) => {
  const taxableAmountNotDetermined = record.get('taxableAmountNotDetermined')
  if (taxableAmountNotDetermined) {
    record.set(
      'taxableAmountNotDetermined',
      convertToUppercase(taxableAmountNotDetermined)
    )
  }
  return record
}

export const formatTotalDistribution = (record) => {
  const totalDistribution = record.get('totalDistribution')
  if (totalDistribution) {
    record.set('totalDistribution', convertToUppercase(totalDistribution))
  }
  return record
}

export const formatEmployeeContributions = (record) => {
  const employeeContributions = record.get('employeeContributions')
  if (employeeContributions) {
    formatAndValidateCurrency(
      employeeContributions,
      'employeeContributions',
      record
    )
  }
  return record
}

export const formatFederalIncomeTax = (record) => {
  const federalIncomeTax = record.get('federalIncomeTax')
  if (federalIncomeTax) {
    formatAndValidatePercentage(federalIncomeTax, 'federalIncomeTax', record)
  }
  return record
}

export const formatFederalIncomeTaxAmount = (record) => {
  const federalIncomeTaxAmount = record.get('federalIncomeTaxAmount')
  if (federalIncomeTaxAmount) {
    formatAndValidateCurrency(
      federalIncomeTaxAmount,
      'federalIncomeTaxAmount',
      record
    )
  }
  return record
}

export const formatPercentageTotalDistribution = (record) => {
  const percentageTotalDistribution = record.get('percentageTotalDistribution')
  if (percentageTotalDistribution) {
    formatAndValidatePercentage(
      percentageTotalDistribution,
      'percentageTotalDistribution',
      record
    )
  }
  return record
}

export const formatDistributionCode = (record) => {
  const distributionCode = record.get('distributionCode')
  if (distributionCode) {
    record.set('distributionCode', convertToUppercase(distributionCode))
  }
  return record
}

export const formatStateTaxWithheld = (record) => {
  const stateTaxWithheld = record.get('stateTaxWithheld')
  if (stateTaxWithheld) {
    formatAndValidatePercentage(stateTaxWithheld, 'stateTaxWithheld', record)
  }
  return record
}

export const formatStateTaxWithheldAmount = (record) => {
  const stateTaxWithheldAmount = record.get('stateTaxWithheldAmount')
  if (stateTaxWithheldAmount) {
    formatAndValidateCurrency(
      stateTaxWithheldAmount,
      'stateTaxWithheldAmount',
      record
    )
  }
  return record
}

export const validatePaymentInstructions = (record) => {
  const transactionType = record.get('transactionType')
  const paymentInstructions = record.get('paymentInstructions')
  if (
    transactionType === BD_TRANSACTION_TYPES.ach &&
    paymentInstructions === 'direct rollover'
  ) {
    record.addError(
      'paymentInstructions',
      'Invalid if ACH & payment instructions is direct rollover'
    )
  }
  return record
}

export const validateWireAchFields = (record) => {
  const transactionType = record.get('transactionType')
  if (
    transactionType === BD_TRANSACTION_TYPES.wire ||
    transactionType === BD_TRANSACTION_TYPES.ach
  ) {
    if (!record.get('wireAch')) {
      record.addError('wireAch', 'Required if wire or ACH, blank if check')
    }
    if (!record.get('abaRouting')) {
      record.addError('abaRouting', 'Required if wire or ACH, blank if check')
    }
    if (!record.get('wireAch2')) {
      record.addError('wireAch2', 'Required if wire or ACH, blank if check')
    }
    if (!record.get('wireAch3')) {
      record.addError('wireAch3', 'Required if wire or ACH, blank if check')
    }
  }
  return record
}

export const validateFfcFields = (record) => {
  const transactionType = record.get('transactionType')
  const paymentInstructions = record.get('paymentInstructions')
  if (
    transactionType === BD_TRANSACTION_TYPES.wire &&
    paymentInstructions === 'direct rollover'
  ) {
    if (!record.get('ffcName')) {
      record.addError(
        'ffcName',
        'Required for wire and direct rollover, blank if check'
      )
    }
    if (!record.get('ffcNumber')) {
      record.addError(
        'ffcNumber',
        'Required for wire and direct rollover, blank if check'
      )
    }
  }
  return record
}

export const validateAccountType = (record) => {
  const transactionType = record.get('transactionType')
  if (
    (transactionType === BD_TRANSACTION_TYPES.wire ||
      transactionType === BD_TRANSACTION_TYPES.ach) &&
    !record.get('accountType')
  ) {
    record.addError('accountType', 'Required if wire or ACH, blank if check')
  }
  return record
}

export const validateCheckPayableInfo = (record) => {
  const transactionType = record.get('transactionType')
  if (
    (transactionType === BD_TRANSACTION_TYPES.check ||
      transactionType === BD_TRANSACTION_TYPES.overnight) &&
    !record.get('checkPayableInfo')
  ) {
    record.addError(
      'checkPayableInfo',
      'Required if check, blank if wire or ACH'
    )
  }
  return record
}

export const validateAccountNumber = (record) => {
  const transactionType = record.get('transactionType')
  const paymentInstructions = record.get('paymentInstructions')
  if (
    (transactionType === BD_TRANSACTION_TYPES.check ||
      transactionType === BD_TRANSACTION_TYPES.overnight) &&
    paymentInstructions === 'direct rollover' &&
    !record.get('accountNumber')
  ) {
    record.addError(
      'accountNumber',
      'Required for check and direct rollover, blank for Wire or ACH'
    )
  }
  return record
}

export const validateAddressForCheck = (record) => {
  const transactionType = record.get('transactionType')
  const paymentInstructions = record.get('paymentInstructions')
  if (
    (transactionType === BD_TRANSACTION_TYPES.check ||
      transactionType === BD_TRANSACTION_TYPES.overnight) &&
    paymentInstructions === 'direct rollover'
  ) {
    if (!record.get('addressForCheck')) {
      record.addError(
        'addressForCheck',
        'Required when check and direct rollover, blank if wire or ACH'
      )
    }
    if (!record.get('cityForCheck')) {
      record.addError(
        'cityForCheck',
        'Required when check and direct rollover, blank if wire or ACH'
      )
    }
    if (!record.get('stateForCheck')) {
      record.addError(
        'stateForCheck',
        'Required when check and direct rollover, blank if wire or ACH'
      )
    }
    if (!record.get('zipForCheck')) {
      record.addError(
        'zipForCheck',
        'Required when check and direct rollover, blank if wire or ACH'
      )
    }
  }
  return record
}

export const validateZipForCheck = (record) => {
  const zipForCheck = record.get('zipForCheck')
  if (zipForCheck) {
    const zipCodeRe = /^\d{5}(?:-\d{4})?$/
    const postalCode = zipForCheck.substring(0, 5)
    if (!zipCodeRe.test(zipForCheck)) {
      record.addError(
        'zipForCheck',
        'US Zip Codes must have format: XXXXX or XXXXX-XXXX'
      )
    } else if (invalidPostalCodes.includes(postalCode)) {
      record.addError('zipForCheck', 'Please enter a valid Zip Code')
    }
  }
  return record
}

export const validateFederalIncomeTax = (record) => {
  const federalIncomeTax = record.get('federalIncomeTax')
  const federalIncomeTaxAmount = record.get('federalIncomeTaxAmount')
  if (federalIncomeTax && federalIncomeTaxAmount) {
    record.addError(
      'federalIncomeTax',
      'Either the Federal income tax withheld (Box 4) - percent (%) or the Federal income tax withheld (Box 4) - ($) must have a value, not both'
    )
    record.addError(
      'federalIncomeTaxAmount',
      'Either the Federal income tax withheld (Box 4) - percent (%) or the Federal income tax withheld (Box 4) - ($) must have a value, not both'
    )
  }
  return record
}

export const validateStateTaxWithheld = (record) => {
  const stateTaxWithheld = record.get('stateTaxWithheld')
  const stateTaxWithheldAmount = record.get('stateTaxWithheldAmount')
  if (stateTaxWithheld && stateTaxWithheldAmount) {
    record.addError(
      'stateTaxWithheld',
      'Either the State tax withheld (Box 14) - percent (%) or the State tax withheld (Box 14) - ($) must have a value, not both'
    )
    record.addError(
      'stateTaxWithheldAmount',
      'Either the State tax withheld (Box 14) - percent (%) or the State tax withheld (Box 14) - ($) must have a value, not both'
    )
  }
  return record
}

// ── Notification Services-specific pure functions ─────────────────────────────

// flatten: private normalisation helper for recordkeeper name matching
const flatten = (s) =>
  s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')

export const validateZipCode = (record) => {
  const zipCode = record.get('zipCode')
  if (zipCode) {
    const zipCodeRe = /^\d{5}(?:-\d{4})?$/
    const postalCode = zipCode.substring(0, 5)
    if (!zipCodeRe.test(zipCode)) {
      record.addError(
        'zipCode',
        'US Zip Codes must have format: XXXXX or XXXXX-XXXX'
      )
    } else if (invalidPostalCodes.includes(postalCode)) {
      record.addError('zipCode', 'Please enter a valid Zip Code')
    }
  }
  return record
}

export const formatRecordkeeper = (record) => {
  const recordkeeper = record.get('recordkeeper')
  if (!recordkeeper) return record
  const flat = flatten(recordkeeper)
  for (const rule of RECORDKEEPER_RULES) {
    const matchTerms = rule.containsAny.map(flatten)
    if (matchTerms.some((match) => flat.includes(match))) {
      if (recordkeeper !== rule.target) record.set('recordkeeper', rule.target)
      return record
    }
  }
  return record
}

export const formatNotificationsCountryCode = (record) => {
  const countryCode = record.get('countryCode')
  if (countryCode) {
    const matchingCountry = findCountry(countryCode)
    if (matchingCountry?.value === US_COUNTRY_VALUE) {
      record.set('countryCode', matchingCountry.value)
    } else {
      record.addError(
        'countryCode',
        'Must be a valid country name or country code'
      )
    }
  }
  return record
}

export const formatBalanceVested = (record) => {
  const balanceVested = record.get(notificationServicesFields.BAL_VESTED.key)
  if (balanceVested) {
    formatAndValidateCurrency(
      balanceVested,
      notificationServicesFields.BAL_VESTED.key,
      record
    )
  }
  return record
}

export const formatBalanceTotal = (record) => {
  const balanceTotal = record.get(notificationServicesFields.BAL_TOTAL.key)
  if (balanceTotal) {
    formatAndValidateCurrency(
      balanceTotal,
      notificationServicesFields.BAL_TOTAL.key,
      record
    )
  }
  return record
}

export const formatDateOfBalance = (record) => {
  const dateOfBalance = record.get(
    notificationServicesFields.DATE_OF_BALANCE.key
  )
  if (dateOfBalance) {
    const parsedDate = parseDateFromString(dateOfBalance.trim())
    if (parsedDate) {
      record.set(
        notificationServicesFields.DATE_OF_BALANCE.key,
        format(parsedDate, 'yyyy/MM/dd')
      )
    } else {
      const sanitized = dateOfBalance.match(numericRegex)?.join('')
      if (sanitized) {
        record.set(
          notificationServicesFields.DATE_OF_BALANCE.key,
          sanitized.replace(/(\d{4})(\d{2})(\d{2})/, '$1/$2/$3')
        )
      }
    }
  }
  return record
}

const dateOfBalanceFormatRegex = /^\d{4}\/\d{2}\/\d{2}$/

export const validateDateOfBalance = (record) => {
  const dateOfBalance = record.get(
    notificationServicesFields.DATE_OF_BALANCE.key
  )
  if (dateOfBalance) {
    const parsed = new Date(dateOfBalance)
    if (!isValid(parsed)) {
      dateOfBalanceFormatRegex.test(dateOfBalance)
        ? record.addError(
            notificationServicesFields.DATE_OF_BALANCE.key,
            'Must be a valid date'
          )
        : record.addError(
            notificationServicesFields.DATE_OF_BALANCE.key,
            'Format must be yyyy/mm/dd'
          )
    }
  }
  return record
}

export const formatReasonForRolloutRequest = (record) => {
  const reason = record.get(notificationServicesFields.REASON_ROLLOUT_REQ.key)
  if (
    reason &&
    (reason.toLowerCase().trim() === 'sbr' ||
      reason.toLowerCase().trim() === 'term')
  ) {
    record.set(
      notificationServicesFields.REASON_ROLLOUT_REQ.key,
      reason.toUpperCase().trim()
    )
  }
  return record
}

export const validateReasonForRolloutRequest = (record) => {
  const reason = record.get(notificationServicesFields.REASON_ROLLOUT_REQ.key)
  const balanceVested = record.get(notificationServicesFields.BAL_VESTED.key)
  const balanceTotal = record.get(notificationServicesFields.BAL_TOTAL.key)
  if (reason) {
    if (reason.toLowerCase().trim() === 'sbr' && !balanceVested) {
      record.addError(
        notificationServicesFields.BAL_VESTED.key,
        'Required for small balance rollout (SBR).'
      )
    }
    if (reason.toLowerCase().trim() === 'term' && !balanceTotal) {
      record.addError(
        notificationServicesFields.BAL_TOTAL.key,
        'Required for terminating plans (TERM).'
      )
    }
  }
  return record
}

export const formatGracePeriod = (record) => {
  const gracePeriod = record.get(
    notificationServicesFields.GRACE_PERIOD_TO_RESPOND.key
  )
  if (gracePeriod) {
    const sanitized = gracePeriod.match(numericRegex)?.join('')
    if (!sanitized || !/^\d+$/.test(sanitized)) {
      record.addError(
        notificationServicesFields.GRACE_PERIOD_TO_RESPOND.key,
        'The amount of days the participant has to respond to the rollout notice and should account for mailing time'
      )
    } else {
      record.set(
        notificationServicesFields.GRACE_PERIOD_TO_RESPOND.key,
        sanitized
      )
    }
  }
  return record
}

export const formatCompanyState = (record) => {
  const companyState = record.get(notificationServicesFields.COMPANY_STATE.key)
  if (companyState) {
    const matchingState = findUSState(companyState)
    if (matchingState) {
      record.set(
        notificationServicesFields.COMPANY_STATE.key,
        matchingState.value
      )
    } else {
      record.addError(
        notificationServicesFields.COMPANY_STATE.key,
        'Must be a valid US state'
      )
    }
  }
  return record
}

export const formatCompanyZipCode = (record) => {
  const companyZip = record.get(notificationServicesFields.COMPANY_ZIP.key)
  if (companyZip) {
    const sanitizedInput = companyZip.replaceAll(/[^a-zA-Z0-9- ]/g, '')
    let sanitizedZipCode = sanitizedInput.match(numericRegex)?.join('')
    if (sanitizedZipCode) {
      record.set(notificationServicesFields.COMPANY_ZIP.key, sanitizedZipCode)
      if (sanitizedZipCode.length === 4 || sanitizedZipCode.length === 8) {
        sanitizedZipCode = '0' + sanitizedZipCode
        record.set(notificationServicesFields.COMPANY_ZIP.key, sanitizedZipCode)
      } else if (
        sanitizedZipCode.length === 3 ||
        sanitizedZipCode.length === 7
      ) {
        sanitizedZipCode = '00' + sanitizedZipCode
        record.set(notificationServicesFields.COMPANY_ZIP.key, sanitizedZipCode)
      } else if (
        sanitizedZipCode.length === 2 ||
        sanitizedZipCode.length === 6
      ) {
        sanitizedZipCode = '000' + sanitizedZipCode
        record.set(notificationServicesFields.COMPANY_ZIP.key, sanitizedZipCode)
      }
    }
    if (sanitizedZipCode && sanitizedZipCode.length > 5) {
      record.set(
        notificationServicesFields.COMPANY_ZIP.key,
        sanitizedZipCode.slice(0, 5) + '-' + sanitizedZipCode.slice(5)
      )
    }
  }
  return record
}

export const validateCompanyZipCode = (record) => {
  const companyZip = record.get(notificationServicesFields.COMPANY_ZIP.key)
  if (companyZip) {
    const zipCodeRe = /^\d{5}(?:-\d{4})?$/
    const postalCode = companyZip.substring(0, 5)
    if (!zipCodeRe.test(companyZip)) {
      record.addError(
        notificationServicesFields.COMPANY_ZIP.key,
        'US Zip Codes must have format: XXXXX or XXXXX-XXXX'
      )
    } else if (invalidPostalCodes.includes(postalCode)) {
      record.addError(
        notificationServicesFields.COMPANY_ZIP.key,
        'Please enter a valid Zip Code'
      )
    }
  }
  return record
}

export const validateCompanyEmail = (record) => {
  const email = record.get(notificationServicesFields.COMPANY_CONTACT_EMAIL.key)
  if (email) {
    const schema = yup.string().email()
    if (!schema.isValidSync(email)) {
      record.addError(
        notificationServicesFields.COMPANY_CONTACT_EMAIL.key,
        'Must be valid email format: example@email.com'
      )
    }
  }
  return record
}
